import { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Form, Input, message } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { throttle } from 'underscore';

import LinkStyleButton from 'pages/common/components/LinkStyleButton';

import { confirmCodeApi, verifyEmailApi } from 'apis/auth/login';
import { setUserState } from 'features/userSlice';

import styles from './AuthCodeForm.module.css';
import Title from './Title';

interface AuthCodeFormProps {
  email: string;
}

function AuthCodeForm({ email }: AuthCodeFormProps) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const confirmCode = () => {
    setIsLoading(true);

    if (!code) {
      setErrMsg('코드를 입력해주세요');
    }

    confirmCodeApi(email, code)
      .then((loginUser) => dispatch(setUserState(loginUser)))
      .catch((errMsg) => {
        if (errMsg !== '') {
          setErrMsg(errMsg);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resendEmail = throttle(() => {
    verifyEmailApi(email)
      .then(() => message.success('메일을 다시 발송하였습니다. 메일함을 확인해주세요.'))
      .catch((errMsg) => {
        if (errMsg !== '') message.error(errMsg);
      });
  }, 10000);

  return (
    <div className="center-align-container full-width">
      <Title
        title="이메일에서 코드를 확인하세요"
        subtitle={{ emphasis: email, normal: '에 코드를 전송했습니다. 코드는 잠시 후에 만료되니 서둘러 입력하세요.' }}
      />

      <Form className={styles.form} disabled={isLoading} onFinish={confirmCode}>
        <Form.Item validateStatus={errMsg ? 'error' : undefined} help={errMsg}>
          <Input
            size="large"
            placeholder="코드를 입력해주세요"
            disabled={isLoading}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setErrMsg('');
            }}
          />
        </Form.Item>
        <Form.Item>
          <Button loading={isLoading} size="large" type="primary" block htmlType="submit" icon={<KeyOutlined />}>
            코드 확인
          </Button>
        </Form.Item>

        <Alert
          className={styles.message}
          message={
            <>
              메일을 받지 못하셨나요? 스팸 메일함을 확인해 보시거나{' '}
              <LinkStyleButton onClick={resendEmail} text="여기" />를 눌러 메일 재발송을 시도해보세요.
            </>
          }
          type="warning"
          showIcon
        />
      </Form>
    </div>
  );
}

export default memo(AuthCodeForm);
